/* Sprint 2R-2C — Admin Cage Management panel.
 *
 * Create cages per route, scan parcels into cages, seal, release. Shows
 * a "fill ratio" (3/12) and blocks seal when scanned < expected.
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, Boxes, Plus, Lock, CheckCircle2, Scan, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function HubCagesPanel() {
  const [cages, setCages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [showCreate, setShowCreate] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/hub/cages");
      setCages(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente bure");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser bure...</div>;

  return (
    <div className="space-y-6" data-testid="hub-cages-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-xl text-brand-dark flex items-center gap-2">
            <Boxes size={20} className="text-brand-red"/> Bur / Cage Management
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            {cages.length} bure i alt · {cages.filter(c => c.status === "released").length} udleveret
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-sm inline-flex items-center gap-2"
          data-testid="cage-create-btn"
        >
          <Plus size={14}/> Opret bur
        </button>
      </div>

      {showCreate && (
        <CreateCageForm onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cages.map((cage) => (
          <CageCard key={cage.id} cage={cage}
                    selected={selected === cage.id}
                    onSelect={() => setSelected(cage.id === selected ? null : cage.id)}
                    onChange={load}/>
        ))}
        {cages.length === 0 && (
          <div className="col-span-3 text-center text-brand-text2 py-12">
            Ingen bure endnu — opret det første bur for en aktiv rute
          </div>
        )}
      </div>
    </div>
  );
}

function CageCard({ cage, selected, onSelect, onChange }) {
  const pct = cage.expected_count > 0 ? Math.min(100, (cage.scanned_count / cage.expected_count) * 100) : 0;
  const statusColor = {
    open: "bg-slate-100 text-slate-700",
    sorting: "bg-amber-100 text-amber-800",
    ready_to_seal: "bg-emerald-100 text-emerald-800",
    sealed: "bg-blue-100 text-blue-800",
    released: "bg-purple-100 text-purple-800",
    picked_up_by_driver: "bg-green-100 text-green-800",
    closed: "bg-gray-200 text-gray-700",
  }[cage.status] || "bg-slate-100 text-slate-700";

  const labelInputRef = React.useRef(null);

  const scanLabel = async () => {
    const v = labelInputRef.current?.value?.trim();
    if (!v) return;
    try {
      const { data } = await adminApi().post(`/admin/hub/cages/${cage.id}/scan-package`, { label_number: v });
      if (data.matched && data.kind === "scanned") {
        toast.success(`Scannet ind: ${data.scanned_count}/${data.expected_count}`);
      } else if (data.kind === "duplicate") {
        toast.info("Pakken er allerede scannet ind");
      } else if (data.kind === "wrong_cage") {
        toast.error(data.message);
      } else {
        toast.error(data.message || "Mismatch");
      }
      labelInputRef.current.value = "";
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan-fejl");
    }
  };

  const seal = async (force = false) => {
    try {
      await adminApi().post(`/admin/hub/cages/${cage.id}/seal`, { force });
      toast.success("Bur forseglet");
      onChange();
    } catch (e) {
      if (e?.response?.status === 400 && e.response.data?.detail?.includes("mangler")) {
        if (window.confirm(`${e.response.data.detail}\n\nVil du tvinge forsegling alligevel?`)) {
          seal(true);
        }
      } else {
        toast.error(e?.response?.data?.detail || "Kunne ikke forsegle");
      }
    }
  };

  const release = async () => {
    try {
      await adminApi().post(`/admin/hub/cages/${cage.id}/release`);
      toast.success("Bur klar til chauffør");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke release");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Slet ${cage.cage_number}?`)) return;
    try {
      await adminApi().delete(`/admin/hub/cages/${cage.id}`);
      toast.success("Bur slettet");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  return (
    <div className={`bg-white border ${selected ? "border-brand-red" : "border-brand"} p-4 transition-shadow`}
         data-testid={`cage-card-${cage.cage_number}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono font-bold text-brand-dark">{cage.cage_number}</div>
        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${statusColor}`}>{cage.status}</span>
      </div>
      <div className="text-xs text-brand-text2 mb-2">
        Rute: <span className="font-mono">{cage.route_number || cage.route_id?.slice(0,8)}</span>
        {cage.hub_zone && <> · Zone <b>{cage.hub_zone}</b></>}
      </div>
      <div className="text-xs text-brand-text2">QR: <span className="font-mono">{cage.qr_payload}</span></div>

      {/* Fill bar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs mb-1">
          <span>Pakker scannet</span>
          <span className="font-bold">{cage.scanned_count} / {cage.expected_count}</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded">
          <div className={`h-full rounded ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }}/>
        </div>
      </div>

      <div className="mt-3 flex gap-2 flex-wrap">
        {cage.status !== "released" && cage.status !== "picked_up_by_driver" && cage.status !== "sealed" && (
          <button onClick={onSelect} className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2 inline-flex items-center gap-1"
                  data-testid={`cage-toggle-scan-${cage.cage_number}`}>
            <Scan size={12}/> Scan pakke
          </button>
        )}
        {(cage.status === "sorting" || cage.status === "ready_to_seal") && (
          <button onClick={() => seal(false)} className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
                  data-testid={`cage-seal-${cage.cage_number}`}>
            <Lock size={12}/> Forsegl
          </button>
        )}
        {cage.status === "sealed" && (
          <button onClick={release} className="text-xs px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                  data-testid={`cage-release-${cage.cage_number}`}>
            <CheckCircle2 size={12}/> Release til chauffør
          </button>
        )}
        {(cage.status === "open" || cage.status === "sorting") && (
          <button onClick={remove} className="text-xs px-2 py-1 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">
            <Trash2 size={12}/>
          </button>
        )}
      </div>

      {selected && (cage.status === "open" || cage.status === "sorting" || cage.status === "ready_to_seal") && (
        <div className="mt-3 p-3 bg-brand-bg2 border border-brand">
          <input
            ref={labelInputRef}
            type="text"
            placeholder="Scan/indtast pakke label..."
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") scanLabel(); }}
            className="w-full px-3 py-2 border border-brand text-sm font-mono"
            data-testid={`cage-scan-input-${cage.cage_number}`}
          />
          <button onClick={scanLabel} className="mt-2 text-xs px-3 py-1 bg-brand-red hover:bg-red-700 text-white">Scan ind</button>
        </div>
      )}
    </div>
  );
}

function CreateCageForm({ onClose, onCreated }) {
  const [routeId, setRouteId] = React.useState("");
  const [zone, setZone] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!routeId) return toast.error("Vælg rute");
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/hub/cages", {
        route_id: routeId, hub_zone: zone || undefined,
      });
      toast.success(`Oprettet: ${data.cage_number}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oprette");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-brand p-4 space-y-3">
      <h3 className="font-display font-bold text-base">Opret bur</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase text-brand-text2 mb-1">Rute ID *</label>
          <input value={routeId} onChange={(e) => setRouteId(e.target.value)}
                 placeholder="UUID for ruten"
                 className="w-full px-3 py-2 border border-brand text-sm font-mono"
                 data-testid="cage-create-route-id"/>
        </div>
        <div>
          <label className="block text-xs uppercase text-brand-text2 mb-1">HUB zone (valgfri)</label>
          <input value={zone} onChange={(e) => setZone(e.target.value)}
                 placeholder="fx A1 eller Reol-3"
                 className="w-full px-3 py-2 border border-brand text-sm"
                 data-testid="cage-create-zone"/>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-sm disabled:opacity-50" data-testid="cage-create-submit">
          {busy ? <Loader2 className="animate-spin inline" size={14}/> : "Opret bur"}
        </button>
        <button onClick={onClose} className="px-4 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm">Annuller</button>
      </div>
      <div className="text-xs text-brand-text2 flex items-start gap-2">
        <AlertCircle size={12} className="mt-0.5 flex-shrink-0"/>
        Bur-nummer genereres automatisk som BUR-{new Date().toISOString().slice(0,10).replace(/-/g,"")}-NNN. QR-kode indeholder krypteret UUID — print både QR og bur-nummer på label.
      </div>
    </div>
  );
}
